import React, { useState, useEffect, useRef } from 'react';
import './DeliveryList.css';
import { API_BASE_URL } from '../config/api';

interface Delivery {
  id: number;
  name: string;
  status: string;
  created_at: string;
  items: DeliveryItem[];
}

interface DeliveryItem {
  id: number;
  name: string;
  required_quantity: number;
  actual_quantity?: number;
  is_completed: boolean;
  notes?: string;
  updated_at: string;
  batches: DeliveryBatch[];
}

interface DeliveryBatch {
  id: number;
  zoho_batch_id: string;
  zoho_batch_number: string;
  quantity_selected: number;
  storage_locations?: DeliveryBatchStorage[];
}

interface DeliveryBatchStorage {
  id: number;
  zoho_storage_id: string;
  storage_name: string;
  warehouse_id: string;
  warehouse_name: string;
  quantity: number;
  total_available_quantity?: number;
}

const DeliveryList: React.FC = () => {
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDeliveryId, setExpandedDeliveryId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    actual_quantity: '',
    is_completed: false,
    notes: ''
  });
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [viewingNotesItem, setViewingNotesItem] = useState<number | null>(null);
  const [processingDelivery, setProcessingDelivery] = useState<number | null>(null);
  const [exportPopupOpen, setExportPopupOpen] = useState<number | null>(null);
  const [exportPopupPosition, setExportPopupPosition] = useState<{x: number, y: number}>({x: 0, y: 0});
  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const fetchDeliveries = async () => {
      try {
        
        const response = await fetch(`${API_BASE_URL}/v1/deliveries`);
        if (!response.ok) {
          
          if (response.status === 404) {
            setDeliveries([]);
            return;
          }
          throw new Error('Failed to fetch deliveries');
        }
        const data = await response.json();
        setDeliveries(data);
      } catch (err: any) {
        
        console.log('Deliveries endpoint not available yet:', err.message);
        setDeliveries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveries();
  }, []);

  const deleteDelivery = async (deliveryId: number) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете тази доставка?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete delivery');
      }
      
      
      setDeliveries(deliveries.filter(delivery => delivery.id !== deliveryId));
    } catch (err: any) {
      setError(`Грешка при изтриване: ${err.message}`);
    }
  };

  const toggleDelivery = (id: number) => {
    setExpandedDeliveryId(expandedDeliveryId === id ? null : id);
  };

  const startEditItem = (item: DeliveryItem) => {
    setEditingItem(item.id);
    setEditForm({
      actual_quantity: item.actual_quantity?.toString() || '',
      is_completed: item.is_completed,
      notes: item.notes || ''
    });
  };

  const cancelEditItem = () => {
    setEditingItem(null);
    setEditForm({
      actual_quantity: '',
      is_completed: false,
      notes: ''
    });
  };

  const confirmCompletionChange = async () => {
    const deliveryId = editingItem;
    if (deliveryId && deliveryId > 0) {
      
      await completeDelivery(deliveryId);
    }
    setShowCompletionPopup(false);
    setEditingItem(null);
  };

  const cancelCompletionChange = () => {
    setShowCompletionPopup(false);
  };

  const saveEditItem = async (deliveryId: number, itemId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actual_quantity: editForm.actual_quantity ? parseInt(editForm.actual_quantity) : null,
          is_completed: editForm.is_completed,
          notes: editForm.notes || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update item');
      }

      
      const updatedResponse = await fetch(`${API_BASE_URL}/v1/deliveries`);
      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setDeliveries(updatedData);
      }
      
      setEditingItem(null);
    } catch (err: any) {
      setError(`Грешка при обновяване: ${err.message}`);
    }
  };

  const removeItemFromDelivery = async (deliveryId: number, itemId: number) => {
    if (!window.confirm('Сигурни ли сте, че искате да премахнете този артикул от доставката?')) {
      return;
    }

    try {
      console.log(`× Frontend: Attempting to delete item ${itemId} from delivery ${deliveryId}`);
      
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}/items/${itemId}`, {
        method: 'DELETE'
      });

      console.log(`× Frontend: Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('× Frontend: Error response:', errorData);
        throw new Error(errorData.message || 'Failed to remove item');
      }

      const responseData = await response.json();
      console.log('× Frontend: Success response:', responseData);

      
      const updatedResponse = await fetch(`${API_BASE_URL}/v1/deliveries`);
      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setDeliveries(updatedData);
      }
      
      console.log('✅ Frontend: Item removed successfully and data refreshed');
    } catch (err: any) {
      console.error('❌ Frontend: Error removing item:', err);
      setError(`Грешка при премахване: ${err.message}`);
    }
  };

  const handleDeliveryStatusChange = (deliveryId: number) => {
    setEditingItem(deliveryId); 
    setShowCompletionPopup(true);
  };

  const completeDelivery = async (deliveryId: number) => {
    setProcessingDelivery(deliveryId);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' })
      });

      if (!response.ok) {
        throw new Error('Failed to complete delivery');
      }

      
      const updatedResponse = await fetch(`${API_BASE_URL}/v1/deliveries`);
      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setDeliveries(updatedData);
      }
    } catch (err: any) {
      setError(`Грешка при завършване на доставката: ${err.message}`);
    } finally {
      setProcessingDelivery(null);
    }
  };

  const uncompleteDelivery = async (deliveryId: number) => {
    setProcessingDelivery(deliveryId);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'pending' }) 
      });

      if (!response.ok) {
        throw new Error('Failed to uncomplete delivery');
      }

      
      const updatedResponse = await fetch(`${API_BASE_URL}/v1/deliveries`);
      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        setDeliveries(updatedData);
      }
    } catch (err: any) {
      setError(`Грешка при отмяна на завършването: ${err.message}`);
    } finally {
      setProcessingDelivery(null);
    }
  };

  const exportDelivery = async (deliveryId: number, deliveryName: string) => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}/export`);
      
      if (!response.ok) {
        throw new Error('Failed to export delivery');
      }
      
      
      const now = new Date();
      const sofiaDate = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Sofia"}));
      const dateStr = sofiaDate.toISOString().split('T')[0]; 
      const timeStr = sofiaDate.toTimeString().split(' ')[0].replace(/:/g, '-'); 
      const filename = `delivery_${deliveryId}_${dateStr}_${timeStr}.xlsx`;
      
      
      const blob = await response.blob();
      
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(`Грешка при експорт: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportForZoho = async (deliveryId: number, deliveryName: string) => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/deliveries/${deliveryId}/export/zoho`);
      
      if (!response.ok) {
        throw new Error('Failed to export delivery for Zoho');
      }
      
      
      const now = new Date();
      const sofiaDate = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Sofia"}));
      const dateStr = sofiaDate.toISOString().split('T')[0]; 
      const timeStr = sofiaDate.toTimeString().split(' ')[0].replace(/:/g, '-'); 
      const filename = `delivery_${deliveryId}_zoho_${dateStr}_${timeStr}.xlsx`;
      
      
      const blob = await response.blob();
      
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(`Грешка при Zoho експорт: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const canCompleteDelivery = (delivery: Delivery): boolean => {
    return delivery.status !== 'completed' && delivery.items.every(item => item.is_completed);
  };

  const getStatusDisplayText = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'Завършена';
      case 'pending':
        return 'Незавършена';
      case 'in_progress':
        return 'В процес';
      default:
        return status;
    }
  };

  const getFilteredDeliveries = (): Delivery[] => {
    return deliveries.filter(delivery => {
      if (activeTab === 'completed') {
        return delivery.status === 'completed';
      } else {
        return delivery.status !== 'completed';
      }
    });
  };

  if (loading) return <div>Зареждане на доставки...</div>;
  if (error) return <div className="error-message">Грешка: {error}</div>;

  const filteredDeliveries = getFilteredDeliveries();

  return (
    <div className="delivery-list-container">
      <h3>Списък на създадените доставки</h3>
      
      {}
      <div className="tabs-container">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Незавършени ({deliveries.filter(d => d.status !== 'completed').length})
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Завършени ({deliveries.filter(d => d.status === 'completed').length})
        </button>
      </div>

      {filteredDeliveries.length === 0 ? (
        <p>Няма {activeTab === 'completed' ? 'завършени' : 'незавършени'} доставки.</p>
      ) : (
        <div className="delivery-accordion">
          {filteredDeliveries.map((delivery) => (
            <div key={delivery.id} className="delivery-item">
              {processingDelivery === delivery.id && (
                <div className="processing-overlay">
                  <div className="progress-bar">
                    <div className="progress-bar-fill"></div>
                  </div>
                  <span className="processing-text">
                    {delivery.status === 'completed' ? 'Отмяна на завършването...' : 'Завършване на доставката...'}
                  </span>
                </div>
              )}
              <div className="delivery-header" onClick={() => toggleDelivery(delivery.id)}>
                <span>{delivery.name || `Доставка #${delivery.id}`}</span>
                <span>{new Date(delivery.created_at).toLocaleString('bg-BG')}</span>
                <span className={`status ${delivery.status}`}>{getStatusDisplayText(delivery.status)}</span>
                <div className="delivery-actions">
                  {canCompleteDelivery(delivery) && (
                    <button 
                      className="complete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeliveryStatusChange(delivery.id);
                      }}
                      title="Завърши доставката"
                      disabled={processingDelivery === delivery.id}
                    >
                      {processingDelivery === delivery.id ? '⏳' : '✓'}
                    </button>
                  )}
                  {delivery.status === 'completed' && (
                    <button 
                      className="uncomplete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        uncompleteDelivery(delivery.id);
                      }}
                      title="Отмени завършването на доставката"
                      disabled={processingDelivery === delivery.id}
                    >
                      {processingDelivery === delivery.id ? '⏳' : '🔄'}
                    </button>
                  )}

                  <button 
                    className="export-button-small export-popup-container"
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = (e.target as HTMLElement).getBoundingClientRect();
                      setExportPopupPosition({
                        x: rect.right + 10,
                        y: rect.top
                      });
                      setExportPopupOpen(exportPopupOpen === delivery.id ? null : delivery.id);
                    }}
                    title="Експорт опции"
                    disabled={isExporting}
                  >
                    {isExporting ? '↓' : '⊞'}
                    <span style={{ marginLeft: '2px', fontSize: '10px' }}>▼</span>
                  </button>

                  <button 
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteDelivery(delivery.id);
                    }}
                    title="Изтрий доставка"
                  >
                    ×
                  </button>
                  <span className="expand-icon">{expandedDeliveryId === delivery.id ? '▲' : '▼'}</span>
                </div>
              </div>
              {expandedDeliveryId === delivery.id && (
                <div className="delivery-details">
                  <h4>Артикули в доставката:</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Име на артикул</th>
                        <th>Изисквано количество</th>
                        <th>Действително количество</th>
                        <th>Статус</th>
                        <th>Избрани партиди</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {delivery.items.map((item) => (
                        <React.Fragment key={item.id}>
                          <tr>
                          <td>{item.name}</td>
                          <td>{item.required_quantity}</td>
                          <td>
                            {editingItem === item.id ? (
                              <input
                                type="number"
                                value={editForm.actual_quantity}
                                onChange={(e) => setEditForm({...editForm, actual_quantity: e.target.value})}
                                className="edit-input"
                                placeholder="Количество"
                              />
                            ) : (
                              item.actual_quantity || 'N/A'
                            )}
                          </td>
                          <td>
                            {editingItem === item.id ? (
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={editForm.is_completed}
                                  onChange={(e) => setEditForm({...editForm, is_completed: e.target.checked})}
                                />
                                Завършен
                              </label>
                            ) : (
                              <span className={`status-badge ${item.is_completed ? 'completed' : 'pending'}`}>
                                {item.is_completed ? 'Завършен' : 'Незавършен'}
                              </span>
                            )}
                          </td>
                          <td>
                            <ul>
                              {item.batches.map((batch) => (
                                <li key={batch.id}>
                                  <strong>{batch.zoho_batch_number}</strong> 
                                  {batch.quantity_selected > 0 && ` (Заявено: ${batch.quantity_selected} бр.)`}
                                  {batch.storage_locations && batch.storage_locations.length > 0 && (
                                    <div className="storage-locations">
                                      <small>Местоположения:</small>
                                      <ul className="storage-list">
                                        {batch.storage_locations.map((storage) => (
                                          <li key={storage.id} className="storage-item">
                                            📦 {storage.storage_name} - 🏢 {storage.warehouse_name} 
                                            ({storage.quantity || 0}
                                            {storage.total_available_quantity !== undefined && storage.total_available_quantity !== null 
                                              ? ` / ${storage.total_available_quantity}` 
                                              : ''} бр.)
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td>
                            {editingItem === item.id ? (
                              <div className="edit-actions">
                                <button 
                                  className="action-button save"
                                  onClick={() => saveEditItem(delivery.id, item.id)}
                                >
                                  💾 Запази
                                </button>
                                <button 
                                  className="action-button cancel"
                                  onClick={cancelEditItem}
                                >
                                  × Откажи
                                </button>
                              </div>
                            ) : (
                              <div className="item-actions">
                                <button 
                                  className="action-button notes"
                                  onClick={() => setViewingNotesItem(viewingNotesItem === item.id ? null : item.id)}
                                  title={item.notes ? "Вижте бележки" : "Няма бележки"}
                                >
                                  📝
                                </button>
                                <button 
                                  className="action-button edit"
                                  onClick={() => startEditItem(item)}
                                >
                                  ✏️ Редактирай
                                </button>
                                <button 
                                  className="action-button remove"
                                  onClick={() => removeItemFromDelivery(delivery.id, item.id)}
                                  disabled={processingDelivery === delivery.id}
                                >
                                  × Премахни
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {viewingNotesItem === item.id && (
                          <tr className="notes-row">
                            <td colSpan={6} className="notes-cell">
                              <div className="notes-content">
                                <strong>📝 Бележки:</strong>
                                <div className="notes-text">
                                  {item.notes || <em>Няма добавени бележки за този артикул.</em>}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {editingItem === item.id && (
                          <tr className="edit-notes-row">
                            <td colSpan={6} className="edit-notes-cell">
                              <div className="edit-notes-content">
                                <label className="notes-label">
                                  <strong>📝 Бележки:</strong>
                                  <textarea
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                                    className="notes-textarea"
                                    placeholder="Добавете бележки за този артикул..."
                                    rows={3}
                                  />
                                </label>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCompletionPopup && (
        <div className="popup-overlay">
          <div className="popup-modal">
            <div className="popup-header">
              <h3>Потвърждение</h3>
            </div>
            <div className="popup-content">
              <div>
                <p><strong>⚠️ Внимание!</strong></p>
                <p>Отбелязването на доставката като "Завършена" ще добави количествата в системата и ще се отрази на инвентара в Zoho.</p>
                <p>Сигурни ли сте, че искате да завършите тази доставка?</p>
              </div>
            </div>
            <div className="popup-actions">
              <button className="popup-button confirm" onClick={confirmCompletionChange}>
                Да
              </button>
              <button className="popup-button cancel" onClick={cancelCompletionChange}>
                Не
              </button>
            </div>
          </div>
        </div>
      )}

      {exportPopupOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
            pointerEvents: 'auto'
          }}
          onClick={() => setExportPopupOpen(null)}
        >
          <div 
            className="export-popup"
            style={{
              position: 'absolute',
              left: exportPopupPosition.x,
              top: exportPopupPosition.y,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              minWidth: '180px',
              overflow: 'hidden',
              animation: 'fadeIn 0.15s ease-out',
              zIndex: 10001,
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '8px 0' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Excel export clicked for delivery:', exportPopupOpen);
                  const deliveryId = exportPopupOpen;
                  if (!deliveryId) return;
                  
                  const deliveryName = deliveries.find(d => d.id === deliveryId)?.name || `Доставка #${deliveryId}`;
                  
                  
                  setExportPopupOpen(null);
                  
                  
                  requestAnimationFrame(() => {
                    exportDelivery(deliveryId, deliveryName);
                  });
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  pointerEvents: 'auto'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                ⊞ Excel експорт
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Zoho export clicked for delivery:', exportPopupOpen);
                  const deliveryId = exportPopupOpen;
                  if (!deliveryId) return;
                  
                  const deliveryName = deliveries.find(d => d.id === deliveryId)?.name || `Доставка #${deliveryId}`;
                  
                  
                  setExportPopupOpen(null);
                  
                  
                  requestAnimationFrame(() => {
                    exportForZoho(deliveryId, deliveryName);
                  });
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  pointerEvents: 'auto'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                ⚙ Zoho експорт
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryList;
